import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DeleteConfirmationModal from "../DeleteConfirmationModal/DeleteConfirmationModal";
import LoaderSpiner from "../LoaderSpiner";
import PhoneNumberList from "../Twilio/PhoneNumberList";
import styles from "./NumberManagement.module.css";

const SECRET_KEY = "rajat";

const NumberTable = ({ numbers, onDelete, onRemoveAssigned }) => (
  <>
    <h5 className={styles.title}>Purchased Phone Numbers</h5>
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Phone Number</th>
          <th>Assigned to agent</th>
          <th>Use Case</th>
          <th>Type</th>
          <th>Created At</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {numbers.map((num) => (
          <tr key={num._id}>
            <td>{num.phoneNumber || "N/A"}</td>
            <td>
              {num.assigned || "N/A"}
              {num.assigned && (
                <button
                  className={styles.removeButton}
                  onClick={() =>
                    onRemoveAssigned(num._id, num.phoneNumber, num.type)
                  }
                >
                  Remove Access
                </button>
              )}
            </td>
            <td>{num.useCase || "N/A"}</td>
            <td>{num.type.charAt(0).toUpperCase() + num.type.slice(1)}</td>
            <td>{new Date(num.createdAt).toLocaleDateString()}</td>
            <td>
              <button
                className={styles.deleteButton}
                onClick={() => onDelete(num._id)}
              >
                Release
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
);

const BuyNumberModal = ({ onNumberPurchased, mode, setModalOpen }) => {
  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.cutButton}>
          <button
            className={styles.deleteButton}
            onClick={() => setModalOpen(false)}
          >
            X
          </button>
        </div>
        <PhoneNumberList
          onNumberSelect={(encryptedPhoneNumber) =>
            onNumberPurchased(encryptedPhoneNumber)
          }
        />
      </div>
    </div>
  );
};

const NumberManagement = () => {
  const location = useLocation();
  const [numbers, setNumbers] = useState([]);
  const [mode, setMode] = useState("inbound");
  const [modalOpen, setModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [numberToDelete, setNumberToDelete] = useState(null);
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(true);

  const API_URL =
    "https://prod.toxboxai.com/api/numbers";
  const PLIVO_LINK_API =
    "https://prod.toxboxai.com/api/plivo/link";
  const PLIVO_APP_ID = "21300548034046081";

  // Set mode based on route
  useEffect(() => {
    if (location.pathname.startsWith("/outbound")) {
      setMode("outbound");
    } else if (location.pathname.startsWith("/inbound")) {
      setMode("inbound");
    }
  }, [location.pathname]);

  // Fetch all numbers
  useEffect(() => {
    fetchNumbers();
  }, []);

  const fetchNumbers = async () => {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      console.error("User ID not found in localStorage");
      setError("User not authenticated.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/get-numbers-by-user/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok)
        throw new Error(`Failed to fetch numbers: ${response.status}`);
      const data = await response.json();
      console.log("Fetched numbers:", data);
      setNumbers(Array.isArray(data.numbers) ? data.numbers : []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching numbers:", error);
      setError("Failed to fetch numbers.");
      setLoading(false);
    }
  };

  // Fetch all numbers
  useEffect(() => {
    fetchNumbers();
  }, []);

  // Handle number purchase
  const handleNumberPurchased = async (phoneNumber) => {
    try {
      const fullPhoneNumber = phoneNumber.startsWith("+")
        ? phoneNumber
        : `+${phoneNumber}`;
      const provider = fullPhoneNumber.startsWith("+91") ? "plivo" : "twilio";

      console.log("Selected phone number:", fullPhoneNumber);
      console.log("Determined provider:", provider);

      // Step 2: Provider-specific logic
      if (provider === "plivo") {
        // India: Use Plivo
        const plivoPayload = {
          number: fullPhoneNumber,
          appId: PLIVO_APP_ID,
        };
        console.log("Calling Plivo API with:", plivoPayload);

        const plivoResponse = await fetch(PLIVO_LINK_API, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(plivoPayload),
        });
        const plivoData = await plivoResponse.json();
        console.log("Plivo API response:", plivoData);

        if (
          !plivoResponse.ok ||
          plivoData.message !==
          "Phone number linked to application successfully"
        ) {
          throw new Error(
            plivoData.error ||
            "Failed to link phone number to Plivo application"
          );
        }
      } else {
        // Other countries: Use Twilio
        const twilioPayload = { phoneNumber: fullPhoneNumber };
        console.log("Calling Twilio API with:", twilioPayload);

        const twilioResponse = await fetch(
          "https://prod.toxboxai.com/api/twilio/buy",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(twilioPayload),
          }
        );
        const twilioData = await twilioResponse.json();
        console.log("Twilio API response:", twilioData);

        if (!twilioResponse.ok) {
          throw new Error(
            twilioData.error || "Failed to buy number from Twilio"
          );
        }
      }

      // Step 3: Save the number in your DB with provider info
      const savePayload = {
        phoneNumber: fullPhoneNumber,
        useCase: mode === "inbound" ? "Receiving calls" : "Making calls",
        type: mode,
        provider,
      };
      console.log("Saving number to backend with:", savePayload);

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(savePayload),
      });
      const saveData = await response.json();
      console.log("Save number API response:", saveData);

      if (!response.ok) {
        throw new Error(saveData.error || "Failed to save number");
      }

      fetchNumbers();
    } catch (error) {
      console.error("Error in handleNumberPurchased:", error);
      setError(error.message || "Failed to process purchased number.");
    }
  };

  const handleDelete = (id) => {
    const number = numbers.find((num) => num._id === id);
    setNumberToDelete(number);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!numberToDelete) return;

    try {
      setIsDeleting(true);
      const response = await fetch(`${API_URL}/${numberToDelete._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok)
        throw new Error(`Failed to delete number: ${response.status}`);
      await fetchNumbers();
      setIsDeleteModalOpen(false);
      setNumberToDelete(null);
    } catch (error) {
      console.error("Error deleting number:", error);
      setError("Failed to delete number.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRemoveAssigned = async (id, phoneNumber, type) => {
    try {
      const response = await axios.put(
        `${API_URL}/${id}`,
        { assigned: null, type, phoneNumber, useCase: null },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      console.log("Assigned agent removed:", response.data);

      setNumbers((prev) =>
        prev.map((num) =>
          num._id === id ? { ...num, assigned: null, useCase: null } : num
        )
      );
    } catch (error) {
      console.error(
        "Error removing assigned agent:",
        error.response?.data?.error || error.message
      );
      setError("Failed to remove assigned agent");
    }
  };

  // Filter numbers by type
  const filteredNumbers = numbers.filter((num) => num.type === mode);

  return (
    <div className={`${styles.container} ${styles[mode]}`}>
      {/* <h1>{mode === 'outbound' ? 'Outbound Numbers' : 'Inbound Numbers'}</h1> */}
      <button
        className={styles.actionButton}
        onClick={() => setModalOpen(true)}
      >
        Buy new number
      </button>
      {error && <p className={styles.error}>{error}</p>}
      {modalOpen && (
        <BuyNumberModal
          setModalOpen={setModalOpen}
          onNumberPurchased={handleNumberPurchased}
          mode={mode}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setNumberToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          phoneNumber={numberToDelete?.phoneNumber || "N/A"}
        />
      )}
      {isDeleting && <LoaderSpiner />}

      {loading ? (
        <LoaderSpiner />
      ) : (
        <div className={styles.tableContainer}>
          {filteredNumbers.length > 0 ? (
            <NumberTable
              numbers={filteredNumbers}
              onDelete={handleDelete}
              onRemoveAssigned={handleRemoveAssigned}
            />
          ) : (
            <p>
              No {mode === "outbound" ? "outbound" : "inbound"} numbers found.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default NumberManagement;
